import {
  isAgentEntry,
  isGroupEntry,
  NotificationEntry,
  NotificationEntryKind,
  NotificationStatus,
  SignalNotificationLog,
} from '@provision/signal-notification-log';

const CHANNEL_LABELS: Record<string, string> = {
  sms: 'SMS',
  // Casing matches the existing channel labels in `useSignalNotificationsList`.
  'phone-call': 'Phone Call',
  email: 'Email',
  slack: 'Slack',
};

const FAILURE_REASONS: Record<string, string> = {
  'invalid-recipient': 'invalid number',
  unknown: 'failed',
};

export const humanize = (value?: string): string => (value ?? '').replace(/[_-]/g, ' ');

/** Unknown channels are humanized, not dropped, so a newer backend's channel still renders. */
export const channelLabel = (channel?: string): string =>
  (channel && CHANNEL_LABELS[channel]) || humanize(channel);

export const failureReason = (reason?: string): string =>
  (reason && FAILURE_REASONS[reason]) || humanize(reason) || 'failed';

export const isScheduled = (entry: NotificationEntry): boolean =>
  entry.status === NotificationStatus.Scheduled;

export const isFallback = (entry: NotificationEntry): boolean =>
  isGroupEntry(entry) && entry.kind === NotificationEntryKind.Fallback;

// Fallback entries are deliberately hidden from the card: most partner Slacks are being shut
// down, so the fallback still runs on the backend but the UI must not advertise it. The entries
// stay in the log for auditing; they're just filtered out of everything the card derives.

/** What already happened — every entry that is not still forecast. Fallbacks are hidden. */
export const historyEntries = (log: SignalNotificationLog): NotificationEntry[] =>
  log.notifications.filter((entry) => !isScheduled(entry) && !isFallback(entry));

/** The forecast — what has not happened yet. Fallbacks are hidden. */
export const scheduledEntries = (log: SignalNotificationLog): NotificationEntry[] =>
  log.notifications.filter((entry) => isScheduled(entry) && !isFallback(entry));

export const nextUp = (log: SignalNotificationLog): NotificationEntry | undefined =>
  scheduledEntries(log)[0];

const iterationsOf = (entries: NotificationEntry[]): Set<number> =>
  new Set(
    entries
      .map((entry) => (entry as { iteration?: unknown }).iteration)
      .filter((iteration): iteration is number => typeof iteration === 'number'),
  );

/** "attempt N of M": counts distinct iterations, since one iteration can emit several entries. */
export const attemptProgress = (
  log: SignalNotificationLog,
): { current: number; total: number } => ({
  current: iterationsOf(historyEntries(log)).size,
  total: iterationsOf(log.notifications).size,
});

/** Leftover `scheduled` entries on a resolved incident are what never fired. */
export const canceledCount = (log: SignalNotificationLog): number =>
  scheduledEntries(log).length;

/** Only entries that actually went out — failed attempts are history, not "sent". */
export const sentCount = (log: SignalNotificationLog): number =>
  historyEntries(log).filter((entry) => entry.status === NotificationStatus.Sent).length;

/** True once the fallback has fired and there is nothing left to forecast. */
export const isExhausted = (log: SignalNotificationLog): boolean =>
  scheduledEntries(log).length === 0 &&
  log.notifications.some((entry) => isFallback(entry) && !isScheduled(entry));

/**
 * `partner` entries are only written when no on-call schedule applies. When every visible
 * entry is that kind (fallbacks ignored), the card skips the timeline entirely.
 */
export const hasNoOnCallSchedule = (log: SignalNotificationLog): boolean => {
  const visible = log.notifications.filter((entry) => !isFallback(entry));
  return (
    visible.length > 0 &&
    visible.every(
      (entry) => isGroupEntry(entry) && entry.kind === NotificationEntryKind.Partner,
    )
  );
};

export const NO_ON_CALL_SCHEDULE_LABEL = 'No on-call schedule configured';

export const entryWho = (entry: NotificationEntry): string => {
  if (isAgentEntry(entry)) return entry.name;
  if (isGroupEntry(entry)) {
    if (entry.kind === NotificationEntryKind.Partner) {
      return NO_ON_CALL_SCHEDULE_LABEL;
    }
    return entry.kind === NotificationEntryKind.Fallback ? 'Fallback' : 'Partner';
  }
  return humanize((entry as { kind?: string }).kind) || 'Notification';
};

/** Slack workspace hosts are not useful in the card — show the channel label instead. */
const recipientLabel = (recipient: { channel: string; destination: string }): string => {
  if (recipient.channel === 'slack') return channelLabel('slack');
  if (recipient.channel === 'email') return recipient.destination;
  return recipient.destination || channelLabel(recipient.channel);
};

/** v1: agent rows show the channel only, never the destination; partner/fallback rows show recipients. */
export const entryDetail = (entry: NotificationEntry): string => {
  if (isAgentEntry(entry)) {
    const label = channelLabel(entry.channel);
    return entry.status === NotificationStatus.Failed
      ? `${label} · ${failureReason(entry.reason)}`
      : label;
  }

  if (isGroupEntry(entry)) {
    return entry.recipients.map(recipientLabel).join(' · ');
  }

  return '';
};

export const secondsUntil = (at: string | undefined, now: number): number | null => {
  if (!at) return null;
  const target = new Date(at).getTime();
  if (Number.isNaN(target)) return null;
  return Math.max(0, Math.round((target - now) / 1000));
};

export const formatCountdown = (seconds: number): string =>
  `${Math.floor(seconds / 60)}:${String(seconds % 60).padStart(2, '0')}`;

/**
 * Next attempt's timing, three real cases: overdue reads "due now" (not "in 0:00", which would
 * claim it's firing now); hours away reads "in Nh Nm" (a business-hours gap is routinely 10+
 * hours); minutes away ticks as m:ss.
 */
export const countdownLabel = (seconds: number): string => {
  if (seconds <= 0) return 'due now';
  if (seconds < 3600) return `in ${formatCountdown(seconds)}`;

  const hours = Math.floor(seconds / 3600);
  const minutes = Math.floor((seconds % 3600) / 60);

  return minutes === 0 ? `in ${hours}h` : `in ${hours}h ${minutes}m`;
};
